// Custom hook for mesh canvas rendering and interaction
import { useEffect, useRef, useState, type RefObject } from 'react';
import type { MeshState, Packet, Node } from '../types';
import { buildKNN, buildRadiusTopology, seededNodes, toPxX, toPxY, toNormX, toNormY, dist2, clampToInnerMargin, rand, generateFloodPath, clamp } from '../utils/meshUtils';

export function useMeshCanvas(
  canvasRef: RefObject<HTMLCanvasElement>,
  nodeCount: number,
  trafficRate: number,
  lineOpacity: number,
  neighborK: number,
  pulseColor: string,
  topologyMode: 'knn' | 'radius',
  maxRange: number,
  regenerateTrigger = 0,
  playgroundMode = false,
  playgroundInteractionMode: 'pan' | 'addNodes' = 'pan',
  zoomLevel = 1.0,
  paused = false,
): { 
  mesh: MeshState; 
  activePackets: number; 
  hoveredNode: number | null; 
  hoveredNodePos: { x: number; y: number } | null;
  hoveredPacket: Packet | null;
  hoveredPacketPos: { x: number; y: number } | null;
} {
  const [mesh, setMesh] = useState<MeshState>(() => {
    const positions = seededNodes(nodeCount);
    const nodes: Node[] = positions.map((n) => ({
      ...n,
      size: rand(0.8, 1.2), // Vary node size slightly
    }));
    const edges = topologyMode === 'radius' 
      ? buildRadiusTopology(positions, maxRange)
      : buildKNN(positions, neighborK);
    return { nodes, edges };
  });
  const [hoveredNode, setHoveredNode] = useState<number | null>(null);
  const [hoveredNodePos, setHoveredNodePos] = useState<{ x: number; y: number } | null>(null);
  const [hoveredPacket, setHoveredPacket] = useState<Packet | null>(null);
  const [hoveredPacketPos, setHoveredPacketPos] = useState<{ x: number; y: number } | null>(null);
  const [activePackets, setActivePackets] = useState(0);
  
  // Animation states
  const animationProgressRef = useRef(0);
  const prevPlaygroundModeRef = useRef(playgroundMode);
  const transitionStartRef = useRef(0);
  const transitionDirectionRef = useRef<'toPlayground' | 'toNormal' | null>(null);

  // Track a version to know when edges changed and purge packets
  const edgesVersionRef = useRef(0);
  const packetsRef = useRef<Packet[]>([]);
  const hoverRef = useRef<number | null>(null);
  const lastSpawnRef = useRef(0);
  const dragNodeRef = useRef<number | null>(null);
  const dragPosRef = useRef<{ x: number; y: number } | null>(null);
  const edgeOpacityRef = useRef<Map<string, number>>(new Map());
  const isDraggingRef = useRef(false);
  
  // Canvas panning state (for playground mode)
  const isPanningRef = useRef(false);
  const panOffsetRef = useRef({ x: 0, y: 0 });
  const panStartRef = useRef({ x: 0, y: 0 });

  // Touch tracking for mobile tap detection
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const touchMovedRef = useRef(false);

  // Rebuild mesh when parameters change or regenerate is triggered
  useEffect(() => {
    const positions = seededNodes(nodeCount);
    const nodes: Node[] = positions.map((n) => ({
      ...n,
      size: rand(0.8, 1.2),
    }));
    const edges = topologyMode === 'radius'
      ? buildRadiusTopology(positions, maxRange)
      : buildKNN(positions, neighborK);
    setMesh({ nodes, edges });
    edgesVersionRef.current++;
    // Clear packets so old edge indices can't reference new, shorter arrays
    packetsRef.current = [];
  }, [nodeCount, neighborK, topologyMode, maxRange, regenerateTrigger]);

  // Resize canvas to device pixel ratio
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const box = canvas.getBoundingClientRect();
      const w = Math.max(1, Math.floor((box.width || window.innerWidth) * dpr));
      const h = Math.max(1, Math.floor((box.height || window.innerHeight) * dpr));
      canvas.width = w;
      canvas.height = h;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [canvasRef]);

  // Mouse interactivity
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      
      // If panning canvas in playground mode
      if (isPanningRef.current && playgroundMode) {
        const dx = e.clientX - panStartRef.current.x;
        const dy = e.clientY - panStartRef.current.y;
        
        // Apply pan with bounds (max 30% shift in any direction)
        panOffsetRef.current = {
          x: clamp(dx, -rect.width * 0.3, rect.width * 0.3),
          y: clamp(dy, -rect.height * 0.3, rect.height * 0.3),
        };
        
        canvas.style.cursor = 'grabbing';
        e.preventDefault();
        return;
      }
      
      // Account for pan offset when converting mouse to canvas coordinates
      const canvasX = e.clientX - rect.left - (playgroundMode ? panOffsetRef.current.x : 0);
      const canvasY = e.clientY - rect.top - (playgroundMode ? panOffsetRef.current.y : 0);
      const x = toNormX(canvasX, rect.width);
      const y = toNormY(canvasY, rect.height);
      
      // If dragging a node, update position in ref for immediate visual feedback
      if (dragNodeRef.current !== null && isDraggingRef.current) {
        dragPosRef.current = {
          x: clamp(x, 0, 1),
          y: clamp(y, 0, 1),
        };
        
        // Change cursor while dragging
        canvas.style.cursor = 'grabbing';
        e.preventDefault();
        return;
      }
      
      // Find closest node
      let bestNode = -1;
      let bestNodeDist = Infinity;
      const nodeHoverRadius = 0.025; // Radius for node hover detection (matches click radius)
      
      for (let i = 0; i < mesh.nodes.length; i++) {
        const d2v = dist2({ x, y }, mesh.nodes[i]);
        if (d2v < bestNodeDist && d2v < nodeHoverRadius * nodeHoverRadius) {
          bestNodeDist = d2v;
          bestNode = i;
        }
      }
      
      // Find closest packet (only when paused for inspection)
      let closestPacket: Packet | null = null;
      let closestPacketDist = Infinity;
      const packetHoverRadius = 0.012; // Smaller, precise radius for packets
      
      if (paused && packetsRef.current.length > 0) {
        for (const pkt of packetsRef.current) {
          if (!pkt.path || pkt.currentHop >= pkt.path.length - 1) continue;
          
          const fromIdx = pkt.path[pkt.currentHop];
          const toIdx = pkt.path[pkt.currentHop + 1];
          
          if (fromIdx === undefined || toIdx === undefined ||
              fromIdx < 0 || fromIdx >= mesh.nodes.length ||
              toIdx < 0 || toIdx >= mesh.nodes.length) continue;
          
          const a = mesh.nodes[fromIdx];
          const b = mesh.nodes[toIdx];
          if (!a || !b) continue;
          
          // Calculate packet position
          const px = a.x + (b.x - a.x) * pkt.t;
          const py = a.y + (b.y - a.y) * pkt.t;
          
          const d2 = dist2({ x, y }, { x: px, y: py });
          if (d2 < closestPacketDist && d2 < packetHoverRadius * packetHoverRadius) {
            closestPacketDist = d2;
            closestPacket = pkt;
          }
        }
      }
      
      // Determine what to hover: prioritize the closest element
      // If both node and packet are detected, show the closest one
      let isOverNode = false;
      let isOverPacket = false;
      
      if (bestNode >= 0 && closestPacket) {
        // Both detected - show the closest one
        if (closestPacketDist < bestNodeDist) {
          isOverPacket = true;
        } else {
          isOverNode = true;
        }
      } else if (bestNode >= 0) {
        isOverNode = true;
      } else if (closestPacket) {
        isOverPacket = true;
      }
      
      // Update hover states
      if (isOverNode) {
        hoverRef.current = bestNode;
        setHoveredNode(bestNode);
        setHoveredNodePos({ x: e.clientX, y: e.clientY });
        setHoveredPacket(null);
        setHoveredPacketPos(null);
      } else if (isOverPacket && closestPacket) {
        hoverRef.current = null;
        setHoveredNode(null);
        setHoveredNodePos(null);
        setHoveredPacket(closestPacket);
        setHoveredPacketPos({ x: e.clientX, y: e.clientY });
      } else {
        hoverRef.current = null;
        setHoveredNode(null);
        setHoveredNodePos(null);
        setHoveredPacket(null);
        setHoveredPacketPos(null);
      }
      
      // Change cursor based on hover state and mode
      if (playgroundMode) {
        if (playgroundInteractionMode === 'pan') {
          canvas.style.cursor = isOverNode ? 'grab' : 'grab';
        } else {
          // addNodes mode - show crosshair (+) for adding nodes
          canvas.style.cursor = isOverNode ? 'grab' : 'crosshair';
        }
      } else {
        // In normal mode, canvas is non-interactive
        canvas.style.cursor = 'default';
      }
    };

    const onLeave = () => {
      hoverRef.current = null;
      setHoveredNode(null);
      setHoveredNodePos(null);
      setHoveredPacket(null);
      setHoveredPacketPos(null);
      // Don't reset drag on leave - let mouseup handle it
    };
    
    const onMouseDown = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      
      // Account for pan offset when converting mouse to canvas coordinates
      const canvasX = e.clientX - rect.left - (playgroundMode ? panOffsetRef.current.x : 0);
      const canvasY = e.clientY - rect.top - (playgroundMode ? panOffsetRef.current.y : 0);
      const x = toNormX(canvasX, rect.width);
      const y = toNormY(canvasY, rect.height);
      
      // Check if clicking on a node (use same radius as hover for consistency)
      const nodeHoverRadius = 0.025; // Slightly larger for easier clicking/dragging
      let clickedOnNode = false;
      
      // Only allow dragging nodes in playground mode
      const allowDrag = playgroundMode;
      
      if (allowDrag) {
      for (let i = 0; i < mesh.nodes.length; i++) {
        const d2v = dist2({ x, y }, mesh.nodes[i]);
        if (d2v < nodeHoverRadius * nodeHoverRadius) {
          dragNodeRef.current = i;
          isDraggingRef.current = true;
          canvas.style.cursor = 'grabbing';
          e.preventDefault(); // Prevent any default behavior
          clickedOnNode = true;
          return;
          }
        }
      }
      
      // In playground mode with pan mode, if not clicking on a node, start canvas panning
      if (playgroundMode && playgroundInteractionMode === 'pan' && !clickedOnNode) {
        isPanningRef.current = true;
        // Store the starting position accounting for current pan offset
        panStartRef.current = { 
          x: e.clientX - panOffsetRef.current.x, 
          y: e.clientY - panOffsetRef.current.y 
        };
        canvas.style.cursor = 'grabbing';
        e.preventDefault();
      }
    };

    const onClick = (e: MouseEvent) => {
      // Only add node if we weren't dragging or panning
      if (dragNodeRef.current !== null || isDraggingRef.current || isPanningRef.current) {
        return;
      }
      
      // Only allow clicks in playground mode
      if (!playgroundMode) {
        return;
      }
      
      // In playground mode with pan mode, don't add nodes on click
      if (playgroundMode && playgroundInteractionMode === 'pan') {
        return;
      }
      
      const rect = canvas.getBoundingClientRect();
      
      // Account for pan offset when converting mouse to canvas coordinates
      const canvasX = e.clientX - rect.left - (playgroundMode ? panOffsetRef.current.x : 0);
      const canvasY = e.clientY - rect.top - (playgroundMode ? panOffsetRef.current.y : 0);
      const x = toNormX(canvasX, rect.width);
      const y = toNormY(canvasY, rect.height);
      
      // Add a new node (don't relax existing nodes - just add the new one)
      const clamped = clampToInnerMargin(x, y);
      const newNode: Node = {
        ...clamped,
        size: rand(0.8, 1.2),
      };
      const nodes = [...mesh.nodes, newNode];
      const edges = topologyMode === 'radius'
        ? buildRadiusTopology(nodes, maxRange)
        : buildKNN(nodes, neighborK);
      setMesh({ nodes, edges });
      edgesVersionRef.current++;
      packetsRef.current = []; // Reset packets so indices are valid
    };
    
    const onMouseUp = () => {
      // Stop canvas panning
      if (isPanningRef.current) {
        isPanningRef.current = false;
        if (canvasRef.current) {
          // Set cursor based on mode
          if (playgroundMode) {
            canvasRef.current.style.cursor = playgroundInteractionMode === 'pan' ? 'grab' : 'crosshair';
          } else {
            // In normal mode, canvas is non-interactive
            canvasRef.current.style.cursor = 'default';
          }
        }
      }
      
      if (isDraggingRef.current && dragNodeRef.current !== null && dragPosRef.current) {
        // Apply the final position to the mesh (only update the dragged node)
        const dragIdx = dragNodeRef.current;
        const newNodes = [...mesh.nodes];
        newNodes[dragIdx] = {
          ...newNodes[dragIdx],
          x: dragPosRef.current.x,
          y: dragPosRef.current.y,
        };
        
        // Recalculate edges based on new positions
        const newEdges = topologyMode === 'radius'
          ? buildRadiusTopology(newNodes, maxRange)
          : buildKNN(newNodes, neighborK);
        
        // Update mesh with final position
        setMesh({ nodes: newNodes, edges: newEdges });
        
        // Clear packets after drag to ensure clean state
        packetsRef.current = [];
        
        // Reset drag state
        isDraggingRef.current = false;
        dragNodeRef.current = null;
        dragPosRef.current = null;
      }
      
      // Reset cursor based on interaction mode
      if (canvasRef.current) {
        if (playgroundMode) {
          canvasRef.current.style.cursor = playgroundInteractionMode === 'pan' ? 'grab' : 'crosshair';
        } else {
          // In normal mode, canvas is non-interactive
          canvasRef.current.style.cursor = 'default';
        }
      }
    };

    // Touch event handlers for mobile devices
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        
        // Store touch start position for tap detection
        touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
        touchMovedRef.current = false;
        
        const mouseEvent = new MouseEvent('mousedown', {
          clientX: touch.clientX,
          clientY: touch.clientY,
          bubbles: true,
          cancelable: true,
        });
        onMouseDown(mouseEvent as any);
        }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        
        // Detect if touch moved significantly (not a tap)
        if (touchStartPosRef.current) {
          const dx = Math.abs(touch.clientX - touchStartPosRef.current.x);
          const dy = Math.abs(touch.clientY - touchStartPosRef.current.y);
          if (dx > 5 || dy > 5) {
            touchMovedRef.current = true;
          }
        }
        
        const mouseEvent = new MouseEvent('mousemove', {
          clientX: touch.clientX,
          clientY: touch.clientY,
          bubbles: true,
          cancelable: true,
        });
        onMove(mouseEvent as any);
        // Prevent scroll on mobile while interacting with canvas
        if (isDraggingRef.current || isPanningRef.current) {
          e.preventDefault();
        }
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      const wasTap = !touchMovedRef.current && touchStartPosRef.current;
      const wasDragging = isDraggingRef.current;
      const wasPanning = isPanningRef.current;
      
      onMouseUp();
      
      // If this was a tap (not a drag/pan) in add mode, add a node
      if (wasTap && !wasDragging && !wasPanning && playgroundMode && playgroundInteractionMode === 'addNodes') {
        const touch = e.changedTouches[0];
        const rect = canvas.getBoundingClientRect();
        const canvasX = touch.clientX - rect.left - (playgroundMode ? panOffsetRef.current.x : 0);
        const canvasY = touch.clientY - rect.top - (playgroundMode ? panOffsetRef.current.y : 0);
        const x = toNormX(canvasX, rect.width);
        const y = toNormY(canvasY, rect.height);
        
        // Add a new node
        const clamped = clampToInnerMargin(x, y);
        const newNode: Node = {
          ...clamped,
          size: rand(0.8, 1.2),
        };
        const nodes = [...mesh.nodes, newNode];
        const edges = topologyMode === 'radius'
          ? buildRadiusTopology(nodes, maxRange)
          : buildKNN(nodes, neighborK);
        setMesh({ nodes, edges });
        edgesVersionRef.current++;
        packetsRef.current = []; // Reset packets so indices are valid
      }
      
      // Reset touch tracking
      touchStartPosRef.current = null;
      touchMovedRef.current = false;
      
      // Prevent click event from firing after touch
      e.preventDefault();
    };

    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('click', onClick);
    
    // Touch event listeners
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onTouchEnd);
    canvas.addEventListener('touchcancel', onTouchEnd);
    
    // Also add document-level mouseup to ensure drag ends even if mouse leaves canvas
    document.addEventListener('mouseup', onMouseUp);
    
    // Update cursor when playgroundInteractionMode or playgroundMode changes
    if (playgroundMode) {
      canvas.style.cursor = playgroundInteractionMode === 'pan' ? 'grab' : 'crosshair';
    } else {
      // In normal mode, canvas is non-interactive
      canvas.style.cursor = 'default';
    }
    
    return () => {
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('click', onClick);
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
      canvas.removeEventListener('touchcancel', onTouchEnd);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [canvasRef, mesh.nodes, mesh.edges, neighborK, playgroundMode, topologyMode, maxRange, playgroundInteractionMode, paused]);

  // Animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    // Color palette and message types
    const messageTypes = [
      { name: 'Default', color: pulseColor },
      { name: 'Position', color: '#10b981' },
      { name: 'Text', color: '#3b82f6' },
      { name: 'Telemetry', color: '#8b5cf6' },
      { name: 'Admin', color: '#f59e0b' },
    ];
    
    // Track mode transitions - DON'T reset entrance animation (only for page load)
    if (prevPlaygroundModeRef.current !== playgroundMode) {
      transitionStartRef.current = performance.now();
      // Keep animationProgressRef at 1 if already completed (don't retrigger entrance animation)
      // Track direction of transition
      transitionDirectionRef.current = playgroundMode ? 'toPlayground' : 'toNormal';
      prevPlaygroundModeRef.current = playgroundMode;
    }

    let raf = 0;
    const tick = (tMs: number) => {
      const t = tMs / 1000;
      const W = canvas.clientWidth;
      const H = canvas.clientHeight;
      
      // Set canvas resolution to match display size and zoom
      if (canvas.width !== W || canvas.height !== H) {
        canvas.width = W;
        canvas.height = H;
      }
      
      // Entrance animation progress (0 to 1 over 2 seconds)
      if (animationProgressRef.current < 1) {
        animationProgressRef.current = Math.min(1, animationProgressRef.current + 0.016 / 2);
      }
      
      // Mode transition progress (0 to 1 over 1.6 seconds - slower for visibility)
      const transitionElapsed = (performance.now() - transitionStartRef.current) / 1000;
      const transitionProgress = Math.min(1, transitionElapsed / 1.6);
      
      // Clear transition direction when animation completes
      if (transitionProgress >= 1 && transitionDirectionRef.current !== null) {
        transitionDirectionRef.current = null;
      }

      // Spawn packets with flood routing paths (only when not paused)
      if (!paused && mesh.edges.length > 0 && mesh.nodes.length > 1 && t - lastSpawnRef.current > 1 / Math.max(trafficRate, 0.01)) {
        lastSpawnRef.current = t;
        const burst = Math.max(1, Math.round(trafficRate / 4));
        for (let i = 0; i < burst; i++) {
          // Meshtastic hop_limit: default 3, max 7
          const hopLimit = Math.floor(rand(3, 8)); // 3-7 hops (realistic Meshtastic range)
          const path = generateFloodPath(mesh.edges, mesh.nodes.length, hopLimit);
          // Validate path - all indices must be within bounds and path has valid edges
          const isValidPath = path.length >= 2 && 
            path.every((idx) => idx >= 0 && idx < mesh.nodes.length) &&
            // Verify each hop is along an actual edge
            path.slice(0, -1).every((nodeIdx, i) => {
              const nextIdx = path[i + 1];
              return mesh.edges.some(
                (e) => (e.a === nodeIdx && e.b === nextIdx) || (e.a === nextIdx && e.b === nodeIdx)
              );
            });
          
          if (isValidPath) {
            const msgType = messageTypes[Math.floor(rand(0, messageTypes.length))];
            packetsRef.current.push({ 
              path,
              currentHop: 0,
              hopLimit,
              hopsRemaining: hopLimit,
              t: 0, 
              speed: 0.8, // Consistent speed for all packets (visual clarity)
              phase: rand(0, Math.PI * 2),
              color: msgType.color,
              messageType: msgType.name,
            });
          }
        }
        if (packetsRef.current.length > 2000) packetsRef.current.splice(0, packetsRef.current.length - 2000);
      }

      // Use static node positions, but apply drag offset if dragging
      const nodePositions = mesh.nodes.map((node, i) => {
        if (isDraggingRef.current && dragNodeRef.current === i && dragPosRef.current) {
          return {
            ...node,
            x: dragPosRef.current.x,
            y: dragPosRef.current.y,
          };
        }
        return node;
      });

      // Integrate packets with hop-based routing (only when not paused)
      if (!paused) {
        for (let i = packetsRef.current.length - 1; i >= 0; i--) {
          const p = packetsRef.current[i];
          
          // Validate packet path indices and that edge exists
          const fromIdx = p.path?.[p.currentHop];
          const toIdx = p.path?.[p.currentHop + 1];
          
          const isValid = p.path && 
                         p.path.length >= 2 && 
                         p.currentHop < p.path.length - 1 &&
                         fromIdx !== undefined && fromIdx >= 0 && fromIdx < mesh.nodes.length &&
                         toIdx !== undefined && toIdx >= 0 && toIdx < mesh.nodes.length &&
                         // Verify edge exists between current and next node
                         mesh.edges.some((e) => 
                           (e.a === fromIdx && e.b === toIdx) || (e.a === toIdx && e.b === fromIdx)
                         );
          
          if (!isValid) {
            packetsRef.current.splice(i, 1);
            continue;
          }
          
          // Move along current hop
          p.t += p.speed * 0.016;
          
          // If completed current hop, move to next
          if (p.t >= 1) {
            p.currentHop++;
            p.hopsRemaining--; // Decrement remaining hops (Meshtastic behavior)
            p.t = 0;
            
            // Remove packet if: reached end of path OR hop limit exhausted
            if (p.currentHop >= p.path.length - 1 || p.hopsRemaining <= 0) {
              packetsRef.current.splice(i, 1);
            }
          }
        }
      }

      // Update active packets count for UI (throttled to every ~100ms)
      if (Math.floor(tMs / 100) !== Math.floor((tMs - 16) / 100)) {
        setActivePackets(packetsRef.current.length);
      }
      
      // Clear
      ctx.clearRect(0, 0, W, H);
      ctx.save(); // Save before transformations
      
      // Mobile scaling factor - make nodes/lines slightly larger on small screens
      const isMobile = window.innerWidth < 768;
      const mobileScale = isMobile ? 1.3 : 1.0;
      
      // Apply zoom and pan transformations (always apply zoom, pan only in playground mode)
      const centerX = W / 2;
      const centerY = H / 2;
      const panX = playgroundMode ? panOffsetRef.current.x : 0;
      const panY = playgroundMode ? panOffsetRef.current.y : 0;
      ctx.translate(centerX + panX, centerY + panY);
      ctx.scale(zoomLevel, zoomLevel);
      ctx.translate(-centerX, -centerY);

      // Calculate edges in real-time if dragging
      let edgesToDraw = mesh.edges;
      if (isDraggingRef.current && dragNodeRef.current !== null && dragPosRef.current) {
        const tempNodes = nodePositions.map((n) => ({ x: n.x, y: n.y }));
        edgesToDraw = topologyMode === 'radius'
          ? buildRadiusTopology(tempNodes, maxRange)
          : buildKNN(tempNodes, neighborK);
      }
      
      // Calculate center for entrance animation (normalized coords)
      const normCenterX = 0.5;
      const normCenterY = 0.5;
      
      // Draw edges with animated opacity for dynamic connections
      ctx.save();
      ctx.lineWidth = 1 * mobileScale;
      const currentEdgeKeys = new Set<string>();
      
      for (const e of edgesToDraw) {
        const a = nodePositions[e.a];
        const b = nodePositions[e.b];
        if (!a || !b) continue;
        
        const edgeKey = `${Math.min(e.a, e.b)}-${Math.max(e.a, e.b)}`;
        currentEdgeKeys.add(edgeKey);
        let currentOpacity = edgeOpacityRef.current.get(edgeKey);
        
        // Initialize new edges at 0 opacity for fade-in
        if (currentOpacity === undefined) {
          currentOpacity = 0;
        }
        
        // Animate opacity towards target (fade in)
        const targetOpacity = 1;
        const fadeSpeed = isDraggingRef.current ? 0.15 : 0.08; // Faster during drag
        currentOpacity += (targetOpacity - currentOpacity) * fadeSpeed;
        edgeOpacityRef.current.set(edgeKey, currentOpacity);
        
        // Calculate distance from center for animations
        const avgX = (a.x + b.x) / 2;
        const avgY = (a.y + b.y) / 2;
        const distFromCenter = Math.sqrt((avgX - normCenterX) ** 2 + (avgY - normCenterY) ** 2);
        
        // Determine which animation to apply
        let animationAlpha = 1;
        
        if (transitionDirectionRef.current !== null) {
          // Mode transition active - use transition animation only
          if (transitionDirectionRef.current === 'toNormal') {
            // Deconstruction: fade out from center (reverse of construction)
            // First half (0-0.8s): fade out from center outward - SLOW for visibility
            if (transitionProgress < 0.5) {
              const deconstructProgress = transitionProgress * 2; // 0 to 1 over first half
              const deconstructThreshold = deconstructProgress * 0.8;
              // Elements closer to center fade first (reversed logic)
              animationAlpha = distFromCenter < deconstructThreshold ? 0 : Math.min(1, (distFromCenter - deconstructThreshold) * 8);
            } 
            // Second half (0.8-1.6s): reconstruct from center outward
            else {
              const reconstructProgress = (transitionProgress - 0.5) * 2; // 0 to 1 over second half
              const reconstructThreshold = reconstructProgress * 0.8;
              animationAlpha = distFromCenter > reconstructThreshold ? 0 : Math.min(1, (reconstructThreshold - distFromCenter) * 5);
            }
          } else if (transitionDirectionRef.current === 'toPlayground') {
            // Construction: fade in from center outward (same as entrance)
            if (transitionProgress < 0.5) {
              const fadeOutProgress = transitionProgress * 2;
              animationAlpha = 1 - fadeOutProgress;
            } else {
              const reconstructProgress = (transitionProgress - 0.5) * 2;
              const reconstructThreshold = reconstructProgress * 0.8;
              animationAlpha = distFromCenter > reconstructThreshold ? 0 : Math.min(1, (reconstructThreshold - distFromCenter) * 5);
            }
          }
        } else if (animationProgressRef.current < 1) {
          // Initial entrance animation (page load only)
          const edgeThreshold = animationProgressRef.current * 0.8;
          animationAlpha = distFromCenter > edgeThreshold ? 0 : Math.min(1, (edgeThreshold - distFromCenter) * 5);
        }
        
        // Check if this edge is connected to the hovered node
        const isHovered = hoverRef.current !== null && (e.a === hoverRef.current || e.b === hoverRef.current);
        
        // Vary opacity based on edge length for depth
        const opacityFactor = Math.max(0.3, 1 - e.len * 2);
        const baseAlpha = lineOpacity * opacityFactor * currentOpacity * animationAlpha;
        
        if (isHovered) {
          // Highlight hovered connections
          ctx.globalAlpha = Math.min(baseAlpha * 3, 1); // Brighten significantly
          ctx.strokeStyle = '#22d3ee'; // cyan-400
          ctx.lineWidth = 2.5 * mobileScale; // Thicker line
        } else {
          ctx.globalAlpha = baseAlpha;
      ctx.strokeStyle = '#a1a1aa'; // zinc-400
          ctx.lineWidth = 1 * mobileScale;
        }
        
        ctx.beginPath();
        ctx.moveTo(toPxX(a.x, W), toPxY(a.y, H));
        ctx.lineTo(toPxX(b.x, W), toPxY(b.y, H));
        ctx.stroke();
      }
      
      // Fade out removed edges
      for (const [key, opacity] of Array.from(edgeOpacityRef.current.entries())) {
        if (!currentEdgeKeys.has(key)) {
          if (opacity > 0.01) {
            const fadeSpeed = isDraggingRef.current ? 0.2 : 0.12; // Faster fade during drag
            edgeOpacityRef.current.set(key, opacity * (1 - fadeSpeed));
            
            // Draw fading edge
            const [aStr, bStr] = key.split('-').map(Number);
            const a = nodePositions[aStr];
            const b = nodePositions[bStr];
            if (a && b) {
              ctx.globalAlpha = lineOpacity * opacity;
              ctx.strokeStyle = '#a1a1aa';
        ctx.beginPath();
        ctx.moveTo(toPxX(a.x, W), toPxY(a.y, H));
        ctx.lineTo(toPxX(b.x, W), toPxY(b.y, H));
        ctx.stroke();
      }
          } else {
            edgeOpacityRef.current.delete(key);
          }
        }
      }
      
      ctx.restore();

      // Draw packets hopping through nodes - only if on valid edges
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      for (const pkt of packetsRef.current) {
        if (!pkt.path || pkt.currentHop >= pkt.path.length - 1) continue;
        
        const fromIdx = pkt.path[pkt.currentHop];
        const toIdx = pkt.path[pkt.currentHop + 1];
        
        // Verify indices are valid
        if (fromIdx === undefined || toIdx === undefined || 
            fromIdx < 0 || fromIdx >= mesh.nodes.length ||
            toIdx < 0 || toIdx >= mesh.nodes.length) {
          continue;
        }
        
        // CRITICAL: Verify this edge actually exists in the current mesh
        const edgeExists = mesh.edges.some((e) => 
          (e.a === fromIdx && e.b === toIdx) || (e.a === toIdx && e.b === fromIdx)
        );
        
        if (!edgeExists) {
          continue; // Don't draw packet if edge doesn't exist
        }
        
        const a = nodePositions[fromIdx];
        const b = nodePositions[toIdx];
        
        if (!a || !b) continue;
        
        // Interpolate position between nodes
        const x = a.x + (b.x - a.x) * pkt.t;
        const y = a.y + (b.y - a.y) * pkt.t;
        
        // Use phase for unique pulse pattern per packet
        const pulseSize = (2.5 + 1.5 * Math.sin(pkt.t * Math.PI + pkt.phase)) * mobileScale;
        const pulseAlpha = 0.7 + 0.3 * Math.sin(pkt.t * Math.PI * 2 + pkt.phase);
        
        // Calculate packet position relative to center for animations
        const packetDistFromCenter = Math.sqrt((x - 0.5) ** 2 + (y - 0.5) ** 2);
        
        // Determine which animation to apply (same as edges)
        let packetAnimationAlpha = 1;
        
        if (transitionDirectionRef.current !== null) {
          // Mode transition active - use transition animation only
          if (transitionDirectionRef.current === 'toNormal') {
            // Deconstruction: fade out from center
            if (transitionProgress < 0.5) {
              const deconstructProgress = transitionProgress * 2;
              const deconstructThreshold = deconstructProgress * 0.8;
              packetAnimationAlpha = packetDistFromCenter < deconstructThreshold ? 0 : Math.min(1, (packetDistFromCenter - deconstructThreshold) * 8);
            } else {
              // Reconstruction
              const reconstructProgress = (transitionProgress - 0.5) * 2;
              const reconstructThreshold = reconstructProgress * 0.8;
              packetAnimationAlpha = packetDistFromCenter > reconstructThreshold ? 0 : Math.min(1, (reconstructThreshold - packetDistFromCenter) * 5);
            }
          } else if (transitionDirectionRef.current === 'toPlayground') {
            // Construction
            if (transitionProgress < 0.5) {
              const fadeOutProgress = transitionProgress * 2;
              packetAnimationAlpha = 1 - fadeOutProgress;
            } else {
              const reconstructProgress = (transitionProgress - 0.5) * 2;
              const reconstructThreshold = reconstructProgress * 0.8;
              packetAnimationAlpha = packetDistFromCenter > reconstructThreshold ? 0 : Math.min(1, (reconstructThreshold - packetDistFromCenter) * 5);
            }
          }
        } else if (animationProgressRef.current < 1) {
          // Initial entrance animation (page load only)
          const packetThreshold = animationProgressRef.current * 0.8;
          packetAnimationAlpha = packetDistFromCenter > packetThreshold ? 0 : Math.min(1, (packetThreshold - packetDistFromCenter) * 5);
        }
        
        const finalPacketAlpha = pulseAlpha * packetAnimationAlpha;
        ctx.globalAlpha = finalPacketAlpha;
        ctx.beginPath();
        ctx.fillStyle = pkt.color;
        ctx.arc(toPxX(x, W), toPxY(y, H), pulseSize, 0, Math.PI * 2);
        ctx.fill();
        
        // Draw subtle trail
        ctx.globalAlpha = finalPacketAlpha * 0.3;
        ctx.arc(toPxX(x, W), toPxY(y, H), pulseSize * 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();

      // Draw nodes with varied sizes and entrance animation
      const hovered = hoverRef.current;
      for (let i = 0; i < mesh.nodes.length; i++) {
        const node = nodePositions[i];
        if (!node) continue;
        
        // Calculate distance from center for animations
        const distFromCenter = Math.sqrt((node.x - normCenterX) ** 2 + (node.y - normCenterY) ** 2);
        
        // Determine which animation to apply (same as edges and packets)
        let nodeAnimationAlpha = 1;
        let nodeAnimationScale = 1;
        
        if (transitionDirectionRef.current !== null) {
          // Mode transition active - use transition animation only
          if (transitionDirectionRef.current === 'toNormal') {
            // Deconstruction: fade out from center
            if (transitionProgress < 0.5) {
              const deconstructProgress = transitionProgress * 2;
              const deconstructThreshold = deconstructProgress * 0.8;
              nodeAnimationAlpha = distFromCenter < deconstructThreshold ? 0 : Math.min(1, (distFromCenter - deconstructThreshold) * 8);
              nodeAnimationScale = distFromCenter < deconstructThreshold ? 0 : Math.min(1, (distFromCenter - deconstructThreshold) * 4);
            } else {
              // Reconstruction
              const reconstructProgress = (transitionProgress - 0.5) * 2;
              const reconstructThreshold = reconstructProgress * 0.8;
              nodeAnimationAlpha = distFromCenter > reconstructThreshold ? 0 : Math.min(1, (reconstructThreshold - distFromCenter) * 8);
              nodeAnimationScale = distFromCenter > reconstructThreshold ? 0 : Math.min(1, (reconstructThreshold - distFromCenter) * 4);
            }
          } else if (transitionDirectionRef.current === 'toPlayground') {
            // Construction
            if (transitionProgress < 0.5) {
              const fadeOutProgress = transitionProgress * 2;
              nodeAnimationAlpha = 1 - fadeOutProgress;
              nodeAnimationScale = 1 - (fadeOutProgress * 0.4);
            } else {
              const reconstructProgress = (transitionProgress - 0.5) * 2;
              const reconstructThreshold = reconstructProgress * 0.8;
              nodeAnimationAlpha = distFromCenter > reconstructThreshold ? 0 : Math.min(1, (reconstructThreshold - distFromCenter) * 8);
              nodeAnimationScale = distFromCenter > reconstructThreshold ? 0 : Math.min(1, (reconstructThreshold - distFromCenter) * 4);
            }
          }
        } else if (animationProgressRef.current < 1) {
          // Initial entrance animation (page load only)
          const nodeThreshold = animationProgressRef.current * 0.8;
          nodeAnimationAlpha = distFromCenter > nodeThreshold ? 0 : Math.min(1, (nodeThreshold - distFromCenter) * 8);
          nodeAnimationScale = distFromCenter > nodeThreshold ? 0 : Math.min(1, (nodeThreshold - distFromCenter) * 4);
        }
        
        const finalAlpha = nodeAnimationAlpha;
        const finalScale = nodeAnimationScale;
        
        if (finalAlpha <= 0) continue;
        
        ctx.globalAlpha = finalAlpha;
        
        const baseRadius = 3 * node.size * mobileScale;
        const r = (i === hovered ? baseRadius * 1.6 : baseRadius) * finalScale;
        
        // Glow for hovered node
        if (i === hovered) {
          ctx.beginPath();
          const grad = ctx.createRadialGradient(toPxX(node.x, W), toPxY(node.y, H), 0, toPxX(node.x, W), toPxY(node.y, H), r * 2.5);
          grad.addColorStop(0, 'rgba(96, 165, 250, 0.3)');
          grad.addColorStop(1, 'rgba(96, 165, 250, 0)');
          ctx.fillStyle = grad;
          ctx.arc(toPxX(node.x, W), toPxY(node.y, H), r * 2.5, 0, Math.PI * 2);
          ctx.fill();
        }
        
        ctx.beginPath();
        ctx.fillStyle = i === hovered ? '#60a5fa' : '#e4e4e7'; // blue-400 : zinc-200
        ctx.arc(toPxX(node.x, W), toPxY(node.y, H), r, 0, Math.PI * 2);
        ctx.fill();
        
        // Subtle border
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
      
      ctx.globalAlpha = 1; // Reset

      // Restore canvas state (undo pan translation)
      ctx.restore();

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [canvasRef, mesh.nodes, mesh.edges, lineOpacity, trafficRate, pulseColor, playgroundMode, zoomLevel, playgroundInteractionMode, paused]);

  return { mesh, activePackets, hoveredNode, hoveredNodePos, hoveredPacket, hoveredPacketPos };
}
